import React from "react";
import { osc, orbitAngle, wobble, stagger } from "../../core/AnimationUtils";

/* ═══════════════════════════════════════════════════════════════
   Energy & Power — 8 ANIMATED templates
   solar_panel, wind_turbine, battery_cell, nuclear_reactor,
   hydroelectric_dam, power_grid_diagram, flame_fire, lightning_storm
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Solar Panel ──────────────────────────────────────── */
export const SolarPanel: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Solar Energy";
  const fillPercent = (props.fillPercent as number) ?? 50;
  /** Seconds since the scene started — drives sun pulse, ray reach, and energy flow. */
  const t = (props.t as number) ?? 0;

  // Sun: gentle breathing radius
  const sunR = osc(t, 2.0, 48, 55);
  // Ray reach oscillates — rays pulse outward in sync with sun
  const rayReach = osc(t, 2.0, 78, 92);

  // Energy flow particles along 3 sun-ray lines toward the panel
  // Each particle travels 0→1 along its path, looping continuously
  const energyParticles = [0, 1, 2].map((i) => {
    const phase = stagger(i, 0.33);
    // normalised position along the ray (0 = near sun, 1 = near panel)
    const pos = ((t / 1.5) + phase) % 1;
    const x1 = 580 - i * 40, y1 = 130 + i * 20;
    const x2 = 350 - i * 30, y2 = 250 + i * 15;
    return { px: x1 + (x2 - x1) * pos, py: y1 + (y2 - y1) * pos, opacity: 0.6 - pos * 0.4 };
  });

  // Output bar brightness pulses
  const outputOpacity = osc(t, 1.8, 0.6, 0.9);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="sp-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d47a1" /><stop offset="100%" stopColor="#42a5f5" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#sp-sky)" rx="16" />
      {/* Sun — radius breathes with t */}
      <circle cx="650" cy="100" r={sunR} fill="#fff9c4" opacity="0.9" />
      {/* Sun rays — reach oscillates */}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
        const rad = a * Math.PI / 180;
        return (
          <line key={a}
            x1={650 + Math.cos(rad) * (sunR + 6)}
            y1={100 + Math.sin(rad) * (sunR + 6)}
            x2={650 + Math.cos(rad) * rayReach}
            y2={100 + Math.sin(rad) * rayReach}
            stroke="#fff9c4" strokeWidth="3" strokeLinecap="round" />
        );
      })}
      {/* Energy photon particles traveling from sun toward panel */}
      {energyParticles.map((p, i) => (
        <circle key={i} cx={p.px} cy={p.py} r="3" fill="#fff9c4" opacity={p.opacity} />
      ))}
      {/* Solar panel (tilted) */}
      <g transform="translate(200,270) skewY(-10)">
        {/* Panel frame */}
        <rect x="0" y="0" width="300" height="180" rx="4" fill="#1a237e" stroke="#283593" strokeWidth="3" />
        {/* Grid cells — brightness shifts with t to suggest charge building */}
        {Array.from({ length: 4 }, (_, r) =>
          Array.from({ length: 6 }, (_, c) => {
            const active = fillPercent > (r * 6 + c) / 24 * 100;
            const cellBrightness = active ? osc(t, 3.0, 0.55, 0.75, stagger(r * 6 + c, 0.08)) : 0;
            return (
              <rect key={`${r}-${c}`} x={8 + c * 48} y={8 + r * 43} width="44" height="39" rx="2"
                fill={active ? "#1565c0" : "#0d47a1"}
                stroke="#283593" strokeWidth="1"
                opacity={active ? cellBrightness + 0.3 : 0.4}
              />
            );
          })
        )}
      </g>
      {/* Stand */}
      <rect x="340" y="450" width="10" height="80" fill="#546e7a" />
      <rect x="310" y="530" width="70" height="8" rx="4" fill="#546e7a" />
      {/* Energy output indicator — pulses */}
      <rect x="600" y="350" width="120" height="30" rx="6" fill="#1c2833" stroke="#4caf50" strokeWidth="1.5" />
      <rect x="604" y="354" width={Math.min(112, fillPercent * 1.12)} height="22" rx="4" fill="#4caf50"
        opacity={outputOpacity} />
      <text x="660" y="370" textAnchor="middle" fill="#ecf0f1" fontSize="11">{Math.round(fillPercent)}% output</text>
      <text x="400" y="575" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Wind Turbine ─────────────────────────────────────── */
export const WindTurbine: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Wind Energy";
  /** Seconds since the scene started — drives blade rotation and wind lines. */
  const t = (props.t as number) ?? 0;

  // Blade rotation: one full revolution every 2 seconds
  const bladeAngle = (t / 2.0) * 360;

  // Wind lines: 4 lines drifting rightward at different speeds
  const windSpeeds = [2.0, 2.3, 2.6, 3.0];
  const windYs = [150, 220, 300, 380];

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="wt-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1565c0" /><stop offset="100%" stopColor="#90caf9" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#wt-sky)" rx="16" />
      {/* Wind lines moving right — purely from t */}
      {windYs.map((y, i) => {
        // offset cycles 0→200 then wraps; gives drifting dash illusion
        const dashOffset = -((t * 60 / windSpeeds[i]) % 200);
        return (
          <line key={i}
            x1={50 + i * 20} y1={y} x2={250 + i * 20} y2={y}
            stroke="#ecf0f1" strokeWidth="1.5" opacity="0.25"
            strokeDasharray="20,10"
            strokeDashoffset={dashOffset} />
        );
      })}
      {/* Tower */}
      <polygon points="390,200 410,200 420,530 380,530" fill="#bdbdbd" stroke="#9e9e9e" strokeWidth="1" />
      {/* Nacelle */}
      <rect x="380" y="185" width="40" height="25" rx="6" fill="#e0e0e0" stroke="#9e9e9e" strokeWidth="1" />
      {/* Blades rotating from t */}
      <g transform={`rotate(${bladeAngle}, 400, 197)`}>
        {[0, 120, 240].map((angle) => (
          <g key={angle} transform={`rotate(${angle}, 400, 197)`}>
            <path d="M400,197 L395,60 Q400,50 405,60 L400,197" fill="#ecf0f1" stroke="#bdbdbd" strokeWidth="1" />
          </g>
        ))}
        <circle cx="400" cy="197" r="8" fill="#e0e0e0" stroke="#9e9e9e" strokeWidth="2" />
      </g>
      {/* Ground */}
      <rect x="0" y="530" width="800" height="70" fill="#2e7d32" opacity="0.5" />
      {/* Energy symbol — brightness pulses */}
      <text x="400" y="500" textAnchor="middle" fill="#f9a825" fontSize="16"
        opacity={osc(t, 1.2, 0.5, 1.0)}>⚡</text>
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Battery Cell ─────────────────────────────────────── */
export const BatteryCell: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Battery";
  const fillPercent = (props.fillPercent as number) ?? 75;
  const activeIndex = (props.activeIndex as number) ?? -1;
  /** Seconds since the scene started — drives electron flow particles and fill pulse. */
  const t = (props.t as number) ?? 0;

  // Electrons: 4 particles moving upward (anode→cathode internal flow)
  const electrons = [0, 1, 2, 3].map((i) => {
    const phase = stagger(i, 0.25); // 0.25s apart
    // cy travels from 480 down to 120 (SVG y decreases upward)
    const rawY = 480 - ((t / 2.0 + phase) % 1) * 360;
    return rawY;
  });

  // Fill level pulses subtly
  const fillOpacity = osc(t, 2.0, 0.5, 0.7);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Battery body */}
      <rect x="250" y="100" width="300" height="400" rx="20" fill="#1c2833" stroke="#546e7a" strokeWidth="4" />
      {/* Terminal */}
      <rect x="360" y="80" width="80" height="25" rx="8" fill="#546e7a" />
      {/* Fill level */}
      <rect x="270" y={500 - fillPercent * 3.8} width="260" height={fillPercent * 3.8} rx="12"
        fill={fillPercent > 50 ? "#4caf50" : fillPercent > 20 ? "#f9a825" : "#e74c3c"}
        opacity={fillOpacity} />
      {/* Charge level marks */}
      {[25, 50, 75].map((pct) => (
        <line key={pct} x1="275" y1={500 - pct * 3.8} x2="545" y2={500 - pct * 3.8}
          stroke="#546e7a" strokeWidth="1" strokeDasharray="6,4" />
      ))}
      {/* Percentage text */}
      <text x="400" y="350" textAnchor="middle" fill="#ecf0f1" fontSize="48" fontWeight="700">{Math.round(fillPercent)}%</text>
      {/* Internal labels */}
      {[
        { y: 130, lbl: "+ Cathode", idx: 0 },
        { y: 310, lbl: "Electrolyte", idx: 1 },
        { y: 470, lbl: "- Anode", idx: 2 },
      ].map((p) => (
        <text key={p.idx} x="610" y={p.y} fill={activeIndex === p.idx ? "#f9a825" : "#7f8c8d"} fontSize="13"
          fontWeight={activeIndex === p.idx ? "600" : "400"}>{p.lbl}</text>
      ))}
      {/* Electron flow — pure t-driven, no SMIL */}
      {fillPercent > 10 && electrons.map((ey, i) => (
        <circle key={i} cx="400" cy={ey} r="4" fill="#42a5f5"
          opacity={ey > 130 && ey < 470 ? 0.8 : 0.0} />
      ))}
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Nuclear Reactor ──────────────────────────────────── */
export const NuclearReactor: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Nuclear Reactor";
  const activeIndex = (props.activeIndex as number) ?? -1;
  /** Seconds since the scene started — drives core glow, turbine spin, steam. */
  const t = (props.t as number) ?? 0;

  // Core: radius breathes, opacity pulses (fission energy)
  const coreR = osc(t, 1.5, 52, 68);
  const coreOpacity = osc(t, 1.5, 0.1, 0.22);

  // Turbine: one revolution per second (fast generator)
  const turbineAngle = orbitAngle(t, 1.0);

  // Steam clouds: 3 puffs rising at staggered phases
  const steamClouds = [0, 1, 2].map((i) => {
    const phase = stagger(i, 0.33);
    const progress = ((t / 3.0) + phase) % 1; // 0→1 in 3s
    return {
      cy: 120 - i * 25 - progress * 60,
      opacity: Math.max(0, (0.2 - i * 0.05) * (1 - progress)),
    };
  });

  // Fuel rods: opacity flickers independently using index-seeded phase
  const fuelRodOpacities = [310, 330, 350, 370, 390].map((_, ri) =>
    osc(t, 1.0, 0.5, 0.95, ri * 0.4)
  );

  // Hot pipe flow — dashoffset crawls forward
  const hotDashOffset = -((t * 40) % 20);
  const coldDashOffset = (t * 40) % 24;

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Containment dome */}
      <path d="M200,400 L200,250 Q200,100 350,100 Q500,100 500,250 L500,400"
        fill={activeIndex === 0 ? "#1c2833" : "#161b22"} stroke="#546e7a" strokeWidth="3" />
      {/* Core glow — radius and opacity breathe from t */}
      <circle cx="350" cy="320" r={coreR} fill="#4caf50" opacity={coreOpacity} />
      {/* Fuel rods — flicker from t */}
      {[310, 330, 350, 370, 390].map((x, ri) => (
        <rect key={x} x={x} y="270" width="8" height="100" rx="2" fill="#f9a825"
          opacity={fuelRodOpacities[ri]} />
      ))}
      {/* Steam generator */}
      <rect x="540" y="150" width="80" height="250" rx="12" fill="#1c2833"
        stroke={activeIndex === 1 ? "#f9a825" : "#546e7a"} strokeWidth="2" />
      {/* Hot pipe (red) — dashoffset advances */}
      <path d="M500,280 L540,280" stroke="#e74c3c" strokeWidth="6"
        strokeDasharray="5,5" strokeDashoffset={hotDashOffset} />
      {/* Cold return pipe (blue) */}
      <path d="M500,320 L540,320" stroke="#3498db" strokeWidth="6"
        strokeDasharray="8,4" strokeDashoffset={coldDashOffset} />
      {/* Cooling tower */}
      <path d="M660,420 Q660,200 700,150 Q740,200 740,420" fill="#1c2833" stroke="#546e7a" strokeWidth="2" />
      {/* Steam puffs from cooling tower — purely from t */}
      {steamClouds.map((s, i) => (
        <ellipse key={i} cx={700 + i * 5} cy={s.cy} rx={15 + i * 8} ry={8 + i * 5}
          fill="#bdbdbd" opacity={s.opacity} />
      ))}
      {/* Turbine — continuous rotation from t */}
      <circle cx="580" cy="440" r="30" fill="#1c2833" stroke="#546e7a" strokeWidth="2" />
      <line
        x1={580 + Math.cos(turbineAngle) * 28}
        y1={440 + Math.sin(turbineAngle) * 28}
        x2={580 - Math.cos(turbineAngle) * 28}
        y2={440 - Math.sin(turbineAngle) * 28}
        stroke="#bdbdbd" strokeWidth="2" />
      <line
        x1={580 + Math.cos(turbineAngle + Math.PI / 2) * 28}
        y1={440 + Math.sin(turbineAngle + Math.PI / 2) * 28}
        x2={580 - Math.cos(turbineAngle + Math.PI / 2) * 28}
        y2={440 - Math.sin(turbineAngle + Math.PI / 2) * 28}
        stroke="#bdbdbd" strokeWidth="2" />
      {/* Ground */}
      <line x1="100" y1="420" x2="750" y2="420" stroke="#546e7a" strokeWidth="2" />
      {/* Labels */}
      <text x="350" y="440" textAnchor="middle" fill="#7f8c8d" fontSize="11">Reactor Core</text>
      <text x="580" y="485" textAnchor="middle" fill="#7f8c8d" fontSize="10">Turbine</text>
      <text x="700" y="440" textAnchor="middle" fill="#7f8c8d" fontSize="10">Cooling</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Hydroelectric Dam ────────────────────────────────── */
export const HydroelectricDam: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Hydroelectric Dam";
  /** Seconds since the scene started — drives water flow, turbine spin, power pulse. */
  const t = (props.t as number) ?? 0;

  // Water surface: gentle ripple encoded as y offset
  const surfaceWave = wobble(t, 3.0, 6);
  const surfaceWave2 = wobble(t, 2.5, 5, 1.2);

  // Turbine rotation: one rev per 1.5 s
  const turbineAngle = orbitAngle(t, 1.5);

  // Downstream water flow particle — travels path 0→1 in 2s
  const flowPos = (t / 2.0) % 1;
  // Path: from x=400,y=350 to x=600,y=410 (approximated linearly)
  const flowX = 400 + flowPos * 200;
  const flowY = 350 + flowPos * 60;

  // Power-line electricity spark
  const sparkX = 420 + ((t / 1.5) % 1) * 280;

  // Turbine blade lines (4-spoke)
  const spokeAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Reservoir water */}
      <rect x="50" y="180" width="300" height="280" fill="#1565c0" opacity="0.4" rx="0" />
      {/* Water surface — ripple from t */}
      <path
        d={`M50,${180 + surfaceWave} Q120,${170 + surfaceWave2} 200,${180 + surfaceWave} Q280,${190 - surfaceWave2} 350,${180 + surfaceWave}`}
        fill="none" stroke="#42a5f5" strokeWidth="2" opacity="0.4" />
      {/* Dam wall */}
      <polygon points="350,120 400,120 420,460 370,460" fill="#546e7a" stroke="#455a64" strokeWidth="2" />
      {/* Water flow through dam — particle */}
      <path d="M400,350 Q450,350 500,380 Q550,410 600,410"
        fill="none" stroke="#1565c0" strokeWidth="8" strokeLinecap="round" opacity="0.5" />
      <circle cx={flowX} cy={flowY} r="5" fill="#42a5f5" opacity="0.8" />
      {/* Turbine — continuous rotation from t */}
      <circle cx="395" cy="350" r="20" fill="#1c2833" stroke="#f9a825" strokeWidth="2" />
      {spokeAngles.map((a, i) => (
        <line key={i}
          x1="395" y1="350"
          x2={395 + Math.cos(turbineAngle + a) * 18}
          y2={350 + Math.sin(turbineAngle + a) * 18}
          stroke="#f9a825" strokeWidth="2" />
      ))}
      {/* Downstream */}
      <rect x="450" y="400" width="300" height="60" fill="#1565c0" opacity="0.25" rx="0" />
      {/* Power lines */}
      <line x1="420" y1="200" x2="700" y2="200" stroke="#bdbdbd" strokeWidth="2" />
      {[500, 600, 700].map((x) => (
        <g key={x}>
          <rect x={x - 3} y="200" width="6" height="260" fill="#546e7a" />
          <rect x={x - 15} y="195" width="30" height="3" fill="#bdbdbd" />
        </g>
      ))}
      {/* Electricity spark moving along power line */}
      <circle cx={sparkX} cy="200" r="3" fill="#f9a825"
        opacity={osc(t, 0.3, 0.3, 1.0)} />
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Power Grid ───────────────────────────────────────── */
export const PowerGridDiagram: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Power Grid";
  /** Seconds since the scene started — drives electricity pulses and window glow. */
  const t = (props.t as number) ?? 0;

  // Electricity pulse: position along the power-line path (0→1 in 3s)
  const pulsePos = (t / 3.0) % 1;
  // The path goes plant→tower1→tower2→transformer; approximate with lerp segments
  // Segment 0: plant(140,260)→tower1(280,210): 0→0.33
  // Segment 1: tower1→tower2(450,210): 0.33→0.66
  // Segment 2: tower2→transformer(620,280): 0.66→1.0
  let sparkX: number, sparkY: number;
  if (pulsePos < 0.33) {
    const p = pulsePos / 0.33;
    sparkX = 140 + p * (280 - 140);
    sparkY = 260 + p * (210 - 260);
  } else if (pulsePos < 0.66) {
    const p = (pulsePos - 0.33) / 0.33;
    sparkX = 280 + p * (450 - 280);
    sparkY = 210;
  } else {
    const p = (pulsePos - 0.66) / 0.34;
    sparkX = 450 + p * (620 - 450);
    sparkY = 210 + p * (280 - 210);
  }

  // Smokestack puff — oscillates
  const puffRy = osc(t, 2.0, 8, 14);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Power plant */}
      <rect x="60" y="250" width="80" height="100" rx="4" fill="#546e7a" stroke="#455a64" strokeWidth="2" />
      <rect x="75" y="220" width="15" height="30" fill="#455a64" />
      {/* Smokestack puff — ry breathes */}
      <ellipse cx="82" cy="210" rx="20" ry={puffRy} fill="#757575" opacity="0.4" />
      <text x="100" y="380" textAnchor="middle" fill="#7f8c8d" fontSize="10">Power Plant</text>
      {/* Transmission towers */}
      {[280, 450].map((x) => (
        <g key={x}>
          <polygon points={`${x - 8},200 ${x + 8},200 ${x + 15},350 ${x - 15},350`} fill="#546e7a" />
          <line x1={x - 25} y1="210" x2={x + 25} y2="210" stroke="#bdbdbd" strokeWidth="2" />
          <line x1={x - 20} y1="230" x2={x + 20} y2="230" stroke="#bdbdbd" strokeWidth="2" />
        </g>
      ))}
      {/* Power lines */}
      <path d="M140,260 Q210,230 280,210" fill="none" stroke="#bdbdbd" strokeWidth="1.5" />
      <path d="M280,210 Q365,190 450,210" fill="none" stroke="#bdbdbd" strokeWidth="1.5" />
      <path d="M450,210 Q550,230 620,280" fill="none" stroke="#bdbdbd" strokeWidth="1.5" />
      {/* Electricity spark — pure t position */}
      <circle cx={sparkX} cy={sparkY} r="4" fill="#f9a825"
        opacity={osc(t, 0.5, 0.4, 1.0)} />
      {/* Transformer */}
      <rect x="600" y="260" width="60" height="60" rx="8" fill="#1c2833" stroke="#f9a825" strokeWidth="2" />
      <text x="630" y="295" textAnchor="middle" fill="#f9a825" fontSize="20">⚡</text>
      <text x="630" y="340" textAnchor="middle" fill="#7f8c8d" fontSize="10">Transformer</text>
      {/* Houses with glowing windows */}
      {[660, 710, 730].map((x, i) => (
        <g key={i}>
          <rect x={x} y={400 + i * 10} width="30" height="25" fill="#1c2833" stroke="#546e7a" strokeWidth="1" />
          <polygon points={`${x - 3},${400 + i * 10} ${x + 15},${385 + i * 10} ${x + 33},${400 + i * 10}`} fill="#546e7a" />
          {/* Window glow — brightness from t, staggered by house index */}
          <rect x={x + 10} y={408 + i * 10} width="10" height="10"
            fill="#f9a825"
            opacity={osc(t, 2.0, 0.5, 0.95, stagger(i, 0.4))} />
        </g>
      ))}
      {/* Distribution lines */}
      <path d="M660,290 L660,400" stroke="#bdbdbd" strokeWidth="1" />
      <path d="M660,400 L710,410 L730,420" stroke="#bdbdbd" strokeWidth="1" />
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Flame & Fire ─────────────────────────────────────── */
export const FlameFire: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Combustion";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const parts = (props.parts as string[]) || ["Fuel", "Heat", "Oxygen"];
  /** Seconds since the scene started — drives flame flicker, spark rise. */
  const t = (props.t as number) ?? 0;

  // Outer flame: width sways left/right, tip height oscillates
  const outerSway = wobble(t, 0.8, 18);     // ± 18 px sway
  const outerTip  = osc(t, 0.8, 110, 130);  // tip y oscillates (lower=taller flame)

  // Middle flame
  const midSway  = wobble(t, 0.65, 14, 0.5);
  const midTip   = osc(t, 0.65, 160, 180);

  // Inner flame (hot core)
  const innerSway = wobble(t, 0.5, 8, 1.0);
  const innerTip  = osc(t, 0.5, 240, 270);

  // Sparks: 5 particles rising, each with own speed derived from index
  const sparks = [0, 1, 2, 3, 4].map((i) => {
    const speed = 1.0 + i * 0.3;
    const pos = ((t / speed) + i * 0.2) % 1; // 0→1
    return {
      cx: 385 + i * 8 + wobble(t, 0.9, 6, i),
      cy: 200 - pos * (100 + i * 15),
      opacity: 1 - pos,
    };
  });

  // Active vertex pulse radius
  const vertexPulse = osc(t, 1.0, 24, 32);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Outer flame — shape driven by t */}
      <path d={`M400,420 Q${350 + outerSway},300 ${380 + outerSway * 0.4},${outerTip} Q400,${outerTip - 10} ${420 - outerSway * 0.4},${outerTip} Q${450 - outerSway},300 400,420`}
        fill="#ff5722" opacity="0.45" />
      {/* Middle flame */}
      <path d={`M400,420 Q${365 + midSway},320 ${385 + midSway * 0.4},${midTip} Q400,${midTip - 10} ${415 - midSway * 0.4},${midTip} Q${435 - midSway},320 400,420`}
        fill="#ff9800" opacity="0.65" />
      {/* Inner flame / core */}
      <path d={`M400,420 Q${380 + innerSway},360 ${390 + innerSway * 0.3},${innerTip} Q400,${innerTip - 8} ${410 - innerSway * 0.3},${innerTip} Q${420 - innerSway},360 400,420`}
        fill="#fff9c4" opacity="0.75" />
      {/* Fire triangle outline */}
      <polygon points="400,80 240,460 560,460" fill="none" stroke="#546e7a" strokeWidth="2" strokeDasharray="8,6" />
      {/* Triangle vertices */}
      {[
        { x: 400, y: 65,  c: "#e74c3c", i: 0 },
        { x: 225, y: 475, c: "#ff9800", i: 1 },
        { x: 575, y: 475, c: "#42a5f5", i: 2 },
      ].map((v) => (
        <g key={v.i}>
          <circle cx={v.x} cy={v.y}
            r={v.i === activeIndex ? vertexPulse : 24}
            fill={v.c}
            opacity={v.i === activeIndex ? 0.9 : 0.4} />
          <text x={v.x} y={v.y + 5} textAnchor="middle" fill="#ecf0f1" fontSize="12" fontWeight="600">{parts[v.i]}</text>
        </g>
      ))}
      {/* Sparks rising — pure t */}
      {sparks.map((s, i) => (
        <circle key={i} cx={s.cx} cy={s.cy} r="2" fill="#fff9c4" opacity={s.opacity} />
      ))}
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Lightning Storm ──────────────────────────────────── */
export const LightningStorm: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Lightning & Storms";
  /** Seconds since the scene started — drives lightning flashes, cloud drift, rain. */
  const t = (props.t as number) ?? 0;

  // Lightning bolt 1: on for a short window every 3s
  const bolt1Phase = (t % 3.0) / 3.0;  // 0→1 every 3s
  const bolt1On = bolt1Phase > 0.6 && bolt1Phase < 0.7;

  // Lightning bolt 2: on for a short window every 4s (offset)
  const bolt2Phase = ((t + 1.5) % 4.0) / 4.0;
  const bolt2On = bolt2Phase > 0.5 && bolt2Phase < 0.58;

  // Background flash tint when bolt fires
  const bgColor = bolt1On || bolt2On ? "#1a237e" : "#0d1117";
  const bgOpacity = bolt1On || bolt2On ? 0.95 : 1.0;

  // Cloud drift — left cloud sways gently
  const cloudDrift = wobble(t, 5.0, 10);

  // Rain: 30 drops, purely t-driven vertical position
  const rainDrops = Array.from({ length: 30 }, (_, i) => {
    const x = 80 + (i * 23) % 640;
    const speed = 0.8 + (i % 5) * 0.1; // varied speeds
    const yBase = 180 + (i % 5) * 20;
    const yRange = 580 - yBase;
    const pos = ((t / speed) + i * 0.05) % 1; // 0→1
    return { x, y1: yBase + pos * yRange, y2: yBase + pos * yRange + 20 };
  });

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill={bgColor} rx="16" opacity={bgOpacity} />
      {/* Storm clouds — left cluster drifts */}
      <g>
        <ellipse cx={300 + cloudDrift} cy="120" rx="150" ry="60" fill="#37474f" />
        <ellipse cx="420" cy="100" rx="120" ry="50" fill="#455a64" />
        <ellipse cx="200" cy="110" rx="100" ry="45" fill="#455a64" />
        <ellipse cx="500" cy="130" rx="130" ry="55" fill="#37474f" />
      </g>
      {/* Lightning bolt 1 — purely t-triggered */}
      <polygon points="350,170 330,270 345,270 310,400 380,260 360,260 390,170"
        fill="#fff9c4" opacity={bolt1On ? 0.95 : 0.05} />
      {/* Lightning bolt 2 */}
      <polygon points="480,150 465,230 475,230 455,320 500,220 490,220 510,150"
        fill="#fff9c4" opacity={bolt2On ? 0.85 : 0.0} />
      {/* Rain — pure t, no SMIL */}
      {rainDrops.map((d, i) => (
        <line key={i} x1={d.x} y1={d.y1} x2={d.x - 5} y2={d.y2}
          stroke="#42a5f5" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
      ))}
      {/* Ground */}
      <rect x="0" y="500" width="800" height="100" fill="#1b5e20" opacity="0.3" />
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
